import React, { useCallback, useContext, useState } from "react";
import requestCache from "../../../utils/requestCache";
import { mutateErrors } from "../../../utils";
import { useSchoolId } from "../../school/hooks/useSchoolId";
import { NotifyMode } from "../../notify/NotifyModeEnum";
import { useNotify } from "../../notify/useNotify";
import { parseToSchoolStudentsFrontEndModel } from "../helpers/parseToSchoolStudentsFrontEndModel";
import useSchoolStudentsQueries from "../hooks/useSchoolStudentsQueries";
import { getFilterOptionsForStudents } from "../helpers/helperFunctions";
import { ISchoolStudentsFrontEndModel } from "../types";

const SchoolStudentsPageContext = React.createContext<
  Partial<ReturnType<typeof useSchoolStudentsPageProvider>>
>({});

export const SchoolStudentsPageProvider: React.FC = ({ children }) => {
  const provider = useSchoolStudentsPageProvider();

  return (
    <SchoolStudentsPageContext.Provider value={provider}>
      {children}
    </SchoolStudentsPageContext.Provider>
  );
};

export const useSchoolStudentsPage = function () {
  return useContext(SchoolStudentsPageContext);
};

const useSchoolStudentsPageProvider = function () {
  const [loading, setLoading] = useState(false);
  const [schoolStudents, setSchoolStudents] = useState<
    ISchoolStudentsFrontEndModel[]
  >([]);
  const { fetchAll } = useSchoolStudentsQueries();
  const snackNotify = useNotify();
  const schoolId = useSchoolId();

  const getSchoolStudents = useCallback(async (data?) => {
    setLoading(true);

    const filterOptions = getFilterOptionsForStudents(data);

    try {
      const { data } = await requestCache<{
        data: ISchoolStudentsFrontEndModel[];
      }>(JSON.stringify(filterOptions) + schoolId, () =>
        fetchAll(filterOptions, schoolId).then((response) =>
          parseToSchoolStudentsFrontEndModel(response),
        ),
      );
      setSchoolStudents(data);
      setLoading(false);
    } catch (e) {
      const { errors, message } = mutateErrors(e);
      snackNotify("Error", message, NotifyMode.ERROR);
      setLoading(false);
      return { ...errors };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    getSchoolStudents,
    schoolStudents,
    loading,
  };
};
